<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import {
  WidgetRatesCurrencyBest,
  WidgetRatesCurrencyChart,
  WidgetRatesCurrencyTable,
  WidgetRatesLinks
} from '~/components/widget/rates'
import { storeToRefs } from 'pinia'
import type { ICurrencyRate } from '~/components/widget/rates'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetFaqList } from '~/components/widget/faq-list'
import { SUPPORTED_CURRENCIES } from '~/data/pages/rates/consts'

const { t } = useI18n()
const currencyStore = useCurrencyStore()
const { list } = storeToRefs(currencyStore)
const route = useRoute()
const { pathWithoutLocale } = useLocalizedPath()

const currencyParam = computed(() => {
  const param = route.params.currency
  return Array.isArray(param) ? param[0]?.toUpperCase() : param?.toUpperCase()
})
if (!SUPPORTED_CURRENCIES.includes(currencyParam.value)) {
  showError({ statusCode: 404 })
}

const breadcrumbs = computed(() => {
  const currency = list.value.find((i) => i.code === currencyParam.value)
  return [
    { title: t('common.exchange_rates'), url: '/rates' },
    { title: currency?.title ?? t('common.currency'), url: route.path }
  ]
})

const chartTitle = computed(() => {
  return t('page.rates.currency.chart_title', { currency: currencyParam.value })
})

const today = useDateFormat(new Date().toISOString())
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-4">
        <div class="space-y-5 xl:col-span-4" role="main">
          <WidgetSeoContent position="upper" :variables="{ today }" />
          <WidgetRatesCurrencyBest :currency="currencyParam.toLowerCase()" />
          <ContentBlock :variant="'white'">
            <WidgetRatesCurrencyTable
              :currency="currencyParam.toLowerCase() as keyof ICurrencyRate"
            />
          </ContentBlock>

          <WidgetSeoContent position="content" />

          <ContentBlock variant="white">
            <WidgetRatesLinks />
          </ContentBlock>

          <ContentBlock variant="white">
            <WidgetRatesCurrencyChart :title="chartTitle" :currency="currencyParam" />
          </ContentBlock>

          <WidgetSeoContent position="bottom" />

          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </div>
    </Container>
  </Main>
</template>
